import { FunctionComponent, memo, useMemo } from "react";
import { IFormValues, IProps } from "./phrase-trigger-form.types";
import { Field, Form, Formik } from "formik";
import SelectInteractionType from "components-for/filters/select-interaction-type";
import { Button, FieldError } from "@edgetier/components";
import { faSearch } from "@fortawesome/pro-solid-svg-icons";
import SelectSearchType from "components-for/filters/select-search-type";
import SelectMessageSource from "./select-message-source";
import * as Yup from "yup";
import { createMessageSourceFromPhraseTrigger } from "utilities-for/phrase-triggers/create-message-source-from-phrase-trigger";
import { SearchType } from "@edgetier/watchtower-types";
import { FieldLabelTooltip } from "@edgetier/client-components";
import SearchTypeTooltipContent from "./search-type-tooltip-content";
import { PhraseTriggerMessageSource } from "./select-message-source/select-message-source.types";
import { getSearchTypeName } from "utilities-for/phrase-triggers/get-search-type-name";
import SelectLanguage from "components-for/filters/select-language";
import { checkParenthesesInString } from "./phrase-trigger-form.utilities";
import { endOfDay, startOfDay, subWeeks } from "date-fns";
import { debounce } from "throttle-debounce";
import { trackUser } from "utilities/track-user";
import { DateFilter } from "@edgetier/date-filter";
import "./phrase-trigger-form.scss";

/**
 * A form for creating or editing a phrase trigger.
 * @param props.initialPhraseTrigger Phrase trigger values that the form should be loaded with.
 * @param props.onSubmit             A function that will run when the form is submitted.
 */
const PhraseTriggerForm: FunctionComponent<IProps> = ({ initialPhraseTrigger, getPreviewResults }) => {
    // Convert inboundMessages, outboundMessages and botMessages to string array so the values can be used in a select.
    const initialValues = useMemo(() => {
        const messageSource = createMessageSourceFromPhraseTrigger(initialPhraseTrigger);
        const interactionTypes = initialPhraseTrigger.interactionTypes.map((interactionType) =>
            interactionType.toUpperCase()
        );
        const dateRangeValues = {
            startDateTime: startOfDay(subWeeks(new Date(), 2)),
            endDateTime: endOfDay(new Date()),
        };
        return { ...initialPhraseTrigger, interactionTypes, messageSource, ...dateRangeValues };
    }, [initialPhraseTrigger]);

    /**
     * Format the data before passing it to the onSubmit function.
     * @param values The submitted form values.
     */
    const preSubmit = (values: IFormValues) => {
        const { messageSource, ...phraseTrigger } = values;
        // The messageSource array needs to be changed to inboundMessages, outboundMessages and botMessages properties.
        getPreviewResults({
            ...phraseTrigger,
            phrase: values.phrase.trim(),
            botMessages: messageSource.includes(PhraseTriggerMessageSource.Bot),
            inboundMessages: messageSource.includes(PhraseTriggerMessageSource.Customer),
            outboundMessages: messageSource.includes(PhraseTriggerMessageSource.Agent),
            searchType: getSearchTypeName(phraseTrigger.searchTypeId),
        });
    };

    return (
        <Formik<IFormValues>
            enableReinitialize
            initialValues={initialValues}
            onSubmit={preSubmit}
            validationSchema={Yup.object().shape({
                interactionTypes: Yup.array().min(1, "Required"),
                messageSource: Yup.array().min(1, "Required"),
                phrase: Yup.mixed().when(["searchTypeId"], (searchTypeId: SearchType) => {
                    if (searchTypeId === SearchType.Similar) {
                        return Yup.string()
                            .matches(/\S+\s+\S+/i, "Must be at least two words")
                            .required("Required");
                    }

                    if (searchTypeId === SearchType.Exact) {
                        return Yup.string()
                            .test({
                                name: "match-parenthesis",
                                exclusive: true,
                                message: "Missing parenthesis",
                                test: (value: string) => checkParenthesesInString(value),
                            })
                            .required("Required");
                    }

                    return Yup.string().required("Required");
                }),
                searchTypeId: Yup.number().required("Required"),
            })}
        >
            {() => (
                <Form name="phrase-trigger-form" className="phrase-trigger-form">
                    <div className="phrase-trigger-form__row">
                        <div className="field">
                            <FieldError name="phrase" />
                            <FieldLabelTooltip
                                label="Phrase"
                                name="phrase"
                                tooltipContent={<div>The phrase you want to match for this tag.</div>}
                            />
                            <Field
                                type="text"
                                name="phrase"
                                id="phrase"
                                placeholder="Phrase&hellip;"
                                autoComplete="off"
                                onKeyUp={() =>
                                    debounce(500, () => {
                                        trackUser("Change phrase trigger");
                                    })
                                }
                            />
                        </div>
                        <div className="field">
                            <FieldLabelTooltip
                                label="Search Type"
                                name="searchTypeId"
                                tooltipContent={<SearchTypeTooltipContent />}
                            />
                            <SelectSearchType />
                        </div>
                        <div className="field">
                            <label className="label" htmlFor="dateRange">
                                <span>Date Range</span>
                            </label>
                            <DateFilter
                                allowTimeSelect
                                showResetButton={false}
                                startDateName="startDateTime"
                                endDateName="endDateTime"
                                hasIcon={false}
                                disabled
                            />
                        </div>
                    </div>
                    <div className="phrase-trigger-form__row">
                        <div className="field">
                            <FieldError name="messageSource" />
                            <FieldLabelTooltip
                                label="Message Source(s)"
                                name="messageSource"
                                tooltipContent={<div>One or more message senders in which to search for phrases.</div>}
                            />
                            <SelectMessageSource isFilter={false} excludeItems={[PhraseTriggerMessageSource.Bot]} />
                        </div>
                        <div className="field">
                            <FieldError name="interactionTypes" />
                            <FieldLabelTooltip
                                label="Interaction Type(s)"
                                name="interactionTypes"
                                tooltipContent={<div>One or more interaction types in which to search for phrase.</div>}
                            />
                            <SelectInteractionType name="interactionTypes" isFilter={false} />
                        </div>

                        <div className="field">
                            <FieldError name="languageIsoCodes" />
                            <FieldLabelTooltip
                                label="Language(s)"
                                name="languageIsoCodes"
                                tooltipContent={<div>One or more languages in which to search for phrases.</div>}
                            />
                            <SelectLanguage name="languageIsoCodes" />
                        </div>
                    </div>
                    <div className="phrase-trigger-form__button-row">
                        <Button icon={faSearch} styleName="primary" outline type="submit">
                            Preview Results
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default memo(PhraseTriggerForm);
